import { Fragment, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { createPortal } from "react-dom";
import { Database } from "lucide-react";
import { adminApi } from "../../api";
import { LoadingText, Select } from "../ui";
import { useAsync } from "../../hooks";
import AuditLogDetailModal from "./AuditLogDetailModal";
import AuditActionIcon from "./AuditActionIcon";
import {
  formatDateTime,
  formatNumber,
  formatTime,
  getAuditActionMeta,
  getDateGroupLabel,
} from "../../utils";
import type { AuditLogPage } from "../../types/audit";

const PAGE_SIZE = 50;

const FILTER_ACTIONS = [
  "chat.query",
  "user.created",
  "user.updated",
  "user.deleted",
  "report_schedule.created",
  "report_schedule.updated",
  "user_report.created",
  "user_report.updated",
  "user_report.deleted",
  "dashboard.created",
  "dashboard.updated",
  "dashboard.deleted",
];

const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All actions" },
  ...FILTER_ACTIONS.map((action) => ({ value: action, label: getAuditActionMeta(action).label })),
];

type AuditUserOption = { id: string; full_name: string | null; email: string };

export default function AuditLog() {
  const [skip, setSkip] = useState(0);
  const [action, setAction] = useState("");
  const [userId, setUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: users } = useAsync<AuditUserOption[]>(
    () => adminApi.listUsers().then((response) => (Array.isArray(response.data) ? response.data : [])),
    [],
  );

  const { data, loading, error } = useAsync<AuditLogPage>(
    () =>
      adminApi
        .getAuditLog({
          skip,
          limit: PAGE_SIZE,
          action: action || undefined,
          userId: userId || undefined,
          dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
          dateTo: dateTo ? new Date(dateTo + "T23:59:59").toISOString() : undefined,
        })
        .then((response) => response.data),
    [skip, action, userId, dateFrom, dateTo],
    { errorMessage: "Failed to load audit log." },
  );

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasFilters = Boolean(action || userId || dateFrom || dateTo);

  function onFilterChange(setValue: Dispatch<SetStateAction<string>>) {
    return (value: string) => { setSkip(0); setValue(value); };
  }

  function clearFilters() {
    setSkip(0);
    setAction("");
    setUserId("");
    setDateFrom("");
    setDateTo("");
  }

  const firstRowNumber = total === 0 ? 0 : skip + 1;
  const lastRowNumber = Math.min(skip + PAGE_SIZE, total);
  const now = new Date();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-gray-500">User</label>
          <Select
            value={userId}
            onChange={(event) => onFilterChange(setUserId)(event.target.value)}
            className="h-9 w-56"
          >
            <option value="">All users</option>
            {(users ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.full_name ? `${user.full_name} (${user.email})` : user.email}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-gray-500">Action</label>
          <Select
            value={action}
            onChange={(event) => onFilterChange(setAction)(event.target.value)}
            className="h-9 w-56"
          >
            {ACTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-gray-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => onFilterChange(setDateFrom)(event.target.value)}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-xs font-medium text-gray-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(event) => onFilterChange(setDateTo)(event.target.value)}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="h-9 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Audit log <span className="font-normal text-gray-400">({formatNumber(total)} entries)</span>
          </h3>
        </div>

        {loading ? (
          <LoadingText className="px-4 py-6" />
        ) : error ? (
          <p className="px-4 py-6 text-sm text-red-500">{error}</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400">No audit entries match these filters.</p>
        ) : (
          <div>
            {rows.map((entry, index) => {
              const group = getDateGroupLabel(entry.created_at, now);
              const prevGroup =
                index > 0 ? getDateGroupLabel(rows[index - 1].created_at, now) : null;
              const header = group !== prevGroup ? group : null;
              return (
                <Fragment key={entry.id}>
                  {header && (
                    <div className="border-t border-gray-100 bg-gray-50 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      {header}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedId(entry.id)}
                    className="flex w-full items-center gap-3 border-t border-gray-100 px-4 py-2.5 text-left hover:bg-gray-50"
                  >
                    <AuditActionIcon action={entry.action} />
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-700">
                      <span className="font-medium text-gray-800">{entry.user_name || "System"}</span>{" "}
                      {getAuditActionMeta(entry.action).verb}
                      {entry.summary && <span className="text-gray-500"> — “{entry.summary}”</span>}
                    </span>
                    {entry.sql_preview && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded px-1 text-[10px] text-gray-400 ring-1 ring-gray-200">
                        <Database size={10} /> SQL
                      </span>
                    )}
                    <time
                      dateTime={entry.created_at || undefined}
                      title={formatDateTime(entry.created_at)}
                      className="shrink-0 text-xs tabular-nums text-gray-400"
                    >
                      {formatTime(entry.created_at)}
                    </time>
                  </button>
                </Fragment>
              );
            })}
          </div>
        )}

        {!loading && !error && total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
            <span>{firstRowNumber}–{lastRowNumber} of {formatNumber(total)}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSkip((currentSkip) => Math.max(0, currentSkip - PAGE_SIZE))}
                disabled={skip === 0}
                className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setSkip((currentSkip) => currentSkip + PAGE_SIZE)}
                disabled={skip + PAGE_SIZE >= total}
                className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId &&
        createPortal(
          <AuditLogDetailModal entryId={selectedId} onClose={() => setSelectedId(null)} />,
          document.body,
        )}
    </div>
  );
}
